
import React from "react";
import { useTranslation } from "react-i18next";
import { ProfilePhotoUploader } from "@/components/ui/profile-photo-uploader";
import { AthleteHeroBackground } from "@/components/athlete/AthleteHeroBackground";
import { Card, CardContent } from "@/components/ui/card";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";

interface ProfileHeaderProps {
  profileData: any;
  userId: string;
  isAdmin: boolean;
  isCoach: boolean;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ profileData, userId, isAdmin, isCoach }) => {
  const { t } = useTranslation(["profile", "common"]);
  const { athleteTheme, athleteBackgrounds } = useThemeCustomization();
  const isPremiumPortal = athleteTheme.variant === "dark-premium" && !isAdmin;
  
  // Get user initials for avatar fallback
  const getInitials = () => {
    if (!profileData) return "?";
    const firstName = profileData.first_name || "";
    const lastName = profileData.last_name || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  if (isPremiumPortal) {
    return (
      <section className="athlete-profile-hero relative overflow-hidden border-b border-primary/50">
        <AthleteHeroBackground src={athleteBackgrounds.profile} className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="athlete-profile-hero-overlay absolute inset-0" />
        <div className="relative z-10 flex min-h-[20rem] flex-col items-center justify-end px-4 pb-5 pt-16 text-center">
          <div className="absolute left-5 top-6 text-left">
            <h1 className="text-3xl font-bold">{t(isCoach ? "coachTitle" : "athleteTitle")}</h1>
            <p className="mt-1 max-w-[15rem] text-sm text-muted-foreground">{t(isCoach ? "coachDescription" : "athleteDescription")}</p>
          </div>
          <ProfilePhotoUploader
            userId={userId}
            initialUrl={profileData?.profile_photo_url}
            editable
            initials={getInitials()}
            featured
          />
          <h2 className="mt-2 text-xl font-bold">
            {profileData ? `${profileData.first_name || ""} ${profileData.last_name || ""}`.trim() : t("loading", { ns: "common" })}
          </h2>
          <span className="mt-1 rounded-sm bg-primary/20 px-2 py-0.5 text-xs font-semibold text-primary">
            {t(isCoach ? "role.coach" : "role.client")}
          </span>
        </div>
      </section>
    );
  }

  return (
    <Card className="bg-card shadow-sm border">
      <CardContent className="p-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <ProfilePhotoUploader 
            userId={userId}
            initialUrl={profileData?.profile_photo_url}
            editable={true}
            initials={getInitials()}
          />
          
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-2xl font-bold">
              {profileData ? `${profileData.first_name || ""} ${profileData.last_name || ""}` : t("loading", { ns: "common" })}
            </h1>
            
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-2">
              {isAdmin && (
                <span className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 text-xs font-medium px-2.5 py-0.5 rounded">
                  {t("role.admin", { ns: "profile" })}
                </span>
              )}
              {isCoach && (
                <span className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 text-xs font-medium px-2.5 py-0.5 rounded">
                  {t("role.coach", { ns: "profile" })}
                </span>
              )}
              {!isAdmin && !isCoach && (
                <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 text-xs font-medium px-2.5 py-0.5 rounded">
                  {t("role.client", { ns: "profile" })}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
